import { CommonModule } from '@angular/common';
import {
    AnimationCallbackEvent,
    Component,
    DestroyRef,
    computed,
    effect,
    ElementRef,
    HostListener,
    inject,
    output,
    signal,
    untracked,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { RaIconComponent } from 'ra-ui';
import { finalize } from 'rxjs';
import { EditDataService } from '../../../services/_edit-data.service';
import { EditItemsService } from '../../../services/edit-items.service';
import { EditService } from '../../../services/edit-service';
import { ChartWizardService } from '../../../services/chart-wizard.service';
import { MenuOptionsService, MenuPlacement } from '../../../services/menu-options.service';
import { TextEditingService } from '../../../services/text-editing.service';

/** Grupo do botão que vira menu: rótulo e opções de uma família de campos. */
interface MorphGroup {
    key: 'text' | 'data' | 'media' | 'more';
    label: string;
    options: { key: string; icon: string; label: string; hint: string; badge?: string; run: () => void }[];
}

@Component({
    selector: 'menu-options',
    templateUrl: 'menu-options.component.html',
    styleUrl: 'menu-options.component.scss',
    imports: [CommonModule, RaIconComponent, TranslatePipe],
})
export class MenuOptionsComponent {
    public openFavoriteFields = output<void>();

    // * [Injects]
    public editService = inject(EditService);
    public editDataService = inject(EditDataService);
    public menuOptionsService = inject(MenuOptionsService);
    private _editItemsService = inject(EditItemsService);
    private _chartWizard = inject(ChartWizardService);
    private _textEditing = inject(TextEditingService);
    private _host = inject<ElementRef<HTMLElement>>(ElementRef);

    /** Onde o menu aparece (fixed), calculado a partir da âncora ao abrir. */
    public position = signal<{ top: number; left: number } | null>(null);

    private static readonly VIEWPORT_MARGIN = 8;

    /** Abrir e fechar do botão que vira menu: mola suave, um pouco mais rápida ao fechar. */
    private static readonly MORPH_IN = { duration: 460, easing: 'cubic-bezier(0.32, 0.72, 0, 1)' };
    private static readonly MORPH_OUT = { duration: 320, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' };

    /** O menu aberto a partir do botão "Inserir campo" é o próprio botão crescendo. */
    public isMorph = computed(() => this.menuOptionsService.placement() === 'morph');
    /** Sem espaço acima do botão, o menu cresce para baixo (botão no topo do painel). */
    public morphDown = signal(false);

    /**
     * Opções do botão que vira menu, agrupadas por família (a cor do ícone vem dela). As
     * mesmas ações do popover; o item do checklist só em modo checklist no nível da seção.
     */
    public morphGroups = computed<MorphGroup[]>(() => {
        const key = (name: string) => `document.edit.menuOptions.${name}`;
        const hint = (name: string) => `document.edit.menuOptions.hints.${name}`;
        const groups: MorphGroup[] = [
            {
                key: 'text',
                label: key('groups.text'),
                options: [
                    { key: 'text', icon: 'text', label: key('text'), hint: hint('text'), run: () => this.addText() },
                    { key: 'callout', icon: 'callout', label: key('callout'), hint: hint('callout'), run: () => this.addCallout() },
                ],
            },
            {
                key: 'data',
                label: key('groups.data'),
                options: [
                    { key: 'stats', icon: 'stats', label: key('stats'), hint: hint('stats'), run: () => this.addStats() },
                    { key: 'table', icon: 'table', label: key('table'), hint: hint('table'), run: () => this.addTable() },
                    { key: 'semaphore', icon: 'semaphore', label: key('semaphore'), hint: hint('semaphore'), run: () => this.addSemaphore() },
                    { key: 'chart', icon: 'chart', label: key('chart'), hint: hint('chart'), badge: key('new'), run: () => this.addChart() },
                ],
            },
            {
                key: 'media',
                label: key('groups.media'),
                options: [
                    { key: 'images', icon: 'gallery', label: key('images'), hint: hint('images'), run: () => this.addImages() },
                    { key: 'videos', icon: 'video-play', label: key('videos'), hint: hint('videos'), run: () => this.addVideo() },
                ],
            },
            {
                key: 'more',
                label: key('groups.more'),
                options: [
                    ...(this.canAddFolder()
                        ? [{ key: 'folder', icon: 'folder', label: 'document.edit.checklist.folder.name', hint: hint('folder'), run: () => this.addFolder() }]
                        : []),
                    { key: 'favorite', icon: 'star', label: key('favoriteField'), hint: hint('favoriteField'), run: () => this.showFavoriteFields() },
                ],
            },
        ];
        return groups;
    });

    constructor() {
        // Abriu (ou trocou de âncora): mede o menu já renderizado e o posiciona; fechou: solta.
        effect(() => {
            const hidden = this.menuOptionsService.isMenuOptionsHidden();
            const anchor = this.menuOptionsService.anchor();
            const placement = this.menuOptionsService.placement();

            untracked(() => {
                if (hidden) {
                    this.position.set(null);
                    return;
                }
                requestAnimationFrame(() => {
                    // Fechou (ou trocou de âncora) antes deste quadro: não mexe no menu que já
                    // está saindo, nem liga o morph depois de fechado.
                    if (this.menuOptionsService.isMenuOptionsHidden()) return;
                    if (this.menuOptionsService.anchor() !== anchor) return;
                    this._place(anchor, placement);
                });
            });
        });

        // O serviço é da raiz: sem isto, sair do editor com o menu aberto (voltar do navegador,
        // atalho) fazia o próximo documento abrir com ele aberto, preso a uma âncora morta.
        inject(DestroyRef).onDestroy(() => {
            if (!this.menuOptionsService.isMenuOptionsHidden()) this.menuOptionsService.hideMenuOptions();
        });
    }

    /**
     * O menu é `fixed` e posicionado uma vez: se a folha rolar pela mão do usuário ou a janela
     * mudar de tamanho, a âncora sai do lugar e ele ficaria solto. Fecha, como os menus do rail.
     */
    @HostListener('document:wheel')
    @HostListener('document:touchmove')
    @HostListener('window:resize')
    public onViewportMove(): void {
        if (!this.menuOptionsService.isMenuOptionsHidden()) this.menuOptionsService.hideMenuOptions();
    }

    /**
     * Sempre que der, para CIMA (pedido do usuário: nada abrindo para baixo sobre a folha).
     * `side`: à ESQUERDA da âncora, na margem cinza ao lado da folha, com a base alinhada à
     * base dela (desce só o que faltar para caber). Sem espaço à esquerda, cai no `below`.
     * `below`: acima da âncora se couber; senão abaixo, alinhado à esquerda dela.
     * `above`: centralizado acima da âncora (o botão flutuante no pé da folha); sem espaço
     * acima, embaixo dela.
     * Sem âncora nenhuma (não deveria acontecer: a barra registra a padrão), canto direito.
     */
    private _place(anchor: HTMLElement | null, placement: MenuPlacement): void {
        if (placement === 'morph' && anchor) {
            this._placeMorph(anchor);
            return;
        }

        // A âncora sumiu (card removido por outra sessão): não há onde abrir.
        if (anchor && !anchor.isConnected) {
            this.menuOptionsService.hideMenuOptions();
            return;
        }

        const margin = MenuOptionsComponent.VIEWPORT_MARGIN;
        const gap = 12;
        // O painel do morph que ainda está saindo fica no DOM por uns 300ms: medir ele daria o
        // tamanho errado para o popover que está entrando.
        const menu = this._host.nativeElement.querySelector<HTMLElement>(
            '.container-menu-options:not(.morph):not(.menu-leave)'
        );
        const width = menu?.offsetWidth || 245;
        const height = menu?.offsetHeight || 360;

        let top: number;
        let left: number;
        if (!anchor) {
            left = window.innerWidth - width - 24;
            top = 96;
        } else {
            const rect = anchor.getBoundingClientRect();
            const sideLeft = rect.left - gap - width;

            if (placement === 'above') {
                left = rect.left + rect.width / 2 - width / 2;
                const above = rect.top - gap - height;
                top = above >= margin ? above : Math.min(rect.bottom + gap, window.innerHeight - margin - height);
            } else if (placement === 'side' && sideLeft >= margin) {
                left = sideLeft;
                top = Math.max(margin, Math.min(rect.bottom - height, window.innerHeight - margin - height));
            } else {
                left = rect.left;
                const above = rect.top - margin - height;
                top = above >= margin ? above : rect.bottom + margin;
                if (top + height > window.innerHeight - margin) {
                    top = Math.max(margin, window.innerHeight - margin - height);
                }
            }
        }

        left = Math.max(margin, Math.min(left, window.innerWidth - width - margin));
        this.position.set({ top, left });
    }

    /**
     * Clique fora do menu fecha. Fora, exceto na âncora e nos gatilhos (`data-menu-trigger`:
     * o botão da barra e as pílulas entre os itens): neles quem decide é o clique deles, que
     * alterna abrir/fechar; se o pointerdown fechasse antes, o clique só saberia reabrir.
     */
    @HostListener('document:pointerdown', ['$event'])
    public onDocumentPointerDown(event: Event): void {
        if (this.menuOptionsService.isMenuOptionsHidden()) return;

        const target = event.target as Node | null;
        if (!target || this._host.nativeElement.contains(target)) return;
        if (this.menuOptionsService.anchor()?.contains(target)) return;
        if ((target as Element).closest?.('[data-menu-trigger]')) return;

        this.menuOptionsService.hideMenuOptions();
    }

    @HostListener('document:keydown.escape')
    public onEscape(): void {
        if (!this.menuOptionsService.isMenuOptionsHidden()) this.menuOptionsService.hideMenuOptions();
    }

    /**
     * Posiciona o painel com a cópia do botão exatamente sobre o botão real e anima o
     * recorte do painel do formato do botão até o tamanho cheio. Tudo direto no elemento,
     * no mesmo quadro: esperar a detecção de mudanças mostraria o painel cheio por um
     * quadro antes de a animação começar.
     */
    private _placeMorph(anchor: HTMLElement): void {
        const margin = MenuOptionsComponent.VIEWPORT_MARGIN;
        if (!anchor.isConnected) {
            this.menuOptionsService.hideMenuOptions();
            return;
        }
        const menu = this._host.nativeElement.querySelector<HTMLElement>(
            '.container-menu-options.morph:not([data-leaving])'
        );
        const surface = menu?.querySelector<HTMLElement>('.morph-surface');
        const button = menu?.querySelector<HTMLElement>('.morph-button');
        if (!menu || !surface || !button) return;

        // Mede o botão PARADO: a entrada com mola (ou o hover) ainda pode estar deslocando e
        // encolhendo ele, e a cópia nasceria no lugar e no tamanho errados.
        anchor.getAnimations().forEach(animation => animation.finish());
        const rect = anchor.getBoundingClientRect();
        const pad = parseFloat(getComputedStyle(surface).paddingLeft) || 8;

        button.style.width = `${rect.width}px`;
        button.style.height = `${rect.height}px`;
        const width = Math.max(menu.offsetWidth, rect.width + pad * 2);
        const height = menu.offsetHeight;

        const left = Math.max(
            margin,
            Math.min(rect.left + rect.width / 2 - width / 2, window.innerWidth - width - margin)
        );
        const down = rect.bottom + pad - height < margin;
        const top = down ? rect.top - pad : rect.bottom + pad - height;

        this.morphDown.set(down);
        menu.classList.toggle('down', down);
        menu.style.width = `${width}px`;
        menu.style.top = `${top}px`;
        menu.style.left = `${left}px`;
        menu.style.visibility = 'visible';
        // A cópia na mesma coluna do botão real, mesmo com o painel encostado na borda.
        button.style.marginLeft = `${rect.left - left - pad}px`;

        this.position.set({ top, left });
        this.menuOptionsService.morphOpen.set(true);

        // O botão real some embaixo do painel e levaria o foco junto: o foco vai para a
        // primeira opção (Tab e Enter continuam funcionando).
        const focusedFab = anchor.contains(document.activeElement);
        if (focusedFab) menu.querySelector<HTMLElement>('.morph-option')?.focus({ preventScroll: true });

        if (this._reducedMotion()) return;
        surface.animate(
            [{ clipPath: this._clipTo(surface, button) }, { clipPath: this._clipFull(surface) }],
            MenuOptionsComponent.MORPH_IN
        );
        // Um respiro de mola no painel inteiro, a partir do botão.
        menu.style.transformOrigin = down ? '50% 0' : '50% 100%';
        menu.animate(
            [{ transform: 'scale(0.98)' }, { transform: 'scale(1.012)', offset: 0.6 }, { transform: 'none' }],
            { duration: 560, easing: MenuOptionsComponent.MORPH_IN.easing }
        );

        // Cabeçalho e grupos entram um de cada vez, de perto do botão para longe, saindo
        // do desfoque. Mais perto do botão, antes.
        const parts = [
            ...menu.querySelectorAll<HTMLElement>('.morph-head, .morph-group'),
        ];
        const ordered = down ? parts : parts.reverse();
        ordered.forEach((part, index) =>
            part.animate(
                [
                    { opacity: 0, transform: `translateY(${down ? -14 : 14}px) scale(0.97)`, filter: 'blur(4px)' },
                    { opacity: 1, transform: 'none', filter: 'blur(0)' },
                ],
                {
                    duration: 420,
                    delay: 110 + index * 55,
                    easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
                    fill: 'backwards',
                }
            )
        );
    }

    /** Fechar: a lista some rápido e o painel encolhe de volta para o botão. */
    public morphOut(event: AnimationCallbackEvent): void {
        const menu = event.target as HTMLElement;
        // Saindo: não é mais "o menu" para medir, e não pega clique (a cópia do botão está
        // exatamente sobre o botão real, e reabrir logo em seguida não fazia nada).
        menu.dataset['leaving'] = '1';
        menu.style.pointerEvents = 'none';
        // Estava com o foco dentro: devolve ao botão que abriu, sem rolar a folha.
        if (menu.contains(document.activeElement)) {
            const fab = this.menuOptionsService.defaultAnchor;
            requestAnimationFrame(() => fab?.focus({ preventScroll: true }));
        }
        const surface = menu.querySelector<HTMLElement>('.morph-surface');
        const button = menu.querySelector<HTMLElement>('.morph-button');
        const content = menu.querySelectorAll<HTMLElement>('.morph-items, .morph-head');
        if (!surface || !button || this._reducedMotion()) {
            event.animationComplete();
            return;
        }

        content.forEach(part =>
            part.animate([{ opacity: 1 }, { opacity: 0, filter: 'blur(3px)' }], { duration: 140, fill: 'forwards' })
        );
        // O texto volta de "Fechar" para "Inserir campo" enquanto encolhe.
        button.classList.add('closing');
        button
            .querySelector('ra-icon')
            ?.animate([{ transform: 'rotate(45deg)' }, { transform: 'none' }], {
                ...MenuOptionsComponent.MORPH_OUT,
                fill: 'forwards',
            });
        surface
            .animate(
                [{ clipPath: this._clipFull(surface) }, { clipPath: this._clipTo(surface, button) }],
                { ...MenuOptionsComponent.MORPH_OUT, fill: 'forwards' }
            )
            .finished.then(
                () => event.animationComplete(),
                () => event.animationComplete()
            );
    }

    /** Recorte do painel no retângulo (e no arredondado) da cópia do botão. */
    private _clipTo(surface: HTMLElement, button: HTMLElement): string {
        const s = surface.getBoundingClientRect();
        const b = button.getBoundingClientRect();
        return `inset(${b.top - s.top}px ${s.right - b.right}px ${s.bottom - b.bottom}px ${b.left - s.left}px round ${b.height / 2}px)`;
    }

    /** Recorte que mostra o painel inteiro, no arredondado dele. */
    private _clipFull(surface: HTMLElement): string {
        const radius = parseFloat(getComputedStyle(surface).borderTopLeftRadius) || 16;
        return `inset(0px 0px 0px 0px round ${radius}px)`;
    }

    private _reducedMotion(): boolean {
        return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    }

    /** "Item do checklist" só aparece em modo checklist e no nível da seção
     *  (pasta não aninha em pasta). */
    public canAddFolder = computed(
        () =>
            (this.editDataService.document()?.config?.checklistMode ?? false) &&
            !this.editDataService.currentFolderId()
    );

    /** A quebra é estrutura da seção; dentro de uma pasta de checklist não faz sentido. */
    public canAddPageBreak = computed(() => !this.editDataService.currentFolderId());

    /**
     * Adiciona uma QUEBRA DE PÁGINA (PAGE_BREAK)
     */
    public addPageBreak(): void {
        this.editService.loading.set(true);
        this._editItemsService
            .addPageBreak(
                this.menuOptionsService.aboveFieldId(),
                this.menuOptionsService.belowFieldId()
            )
            .pipe(finalize(() => this.editService.loading.set(false)))
            .subscribe({
                next: () => {},
                error: error => console.error(error),
            });

        this.menuOptionsService.hideMenuOptions();
    }

    /**
     * Adiciona uma pasta de checklist (CHECKLIST_FOLDER)
     */
    public addFolder(): void {
        this.editService.loading.set(true);
        this._editItemsService
            .addChecklistFolder()
            .pipe(finalize(() => this.editService.loading.set(false)))
            .subscribe({
                next: () => {},
                error: error => console.error(error),
            });

        this.menuOptionsService.hideMenuOptions();
    }

    /**
     * Adiciona um texto
     */
    public addText(subtitle?: boolean): void {
        this.editService.loading.set(true);
        this._editItemsService
            .addText(
                this.menuOptionsService.aboveFieldId(),
                this.menuOptionsService.belowFieldId(),
                subtitle
            )
            .subscribe({
                next: response => {
                    this.editService.loading.set(false);
                    // Entra em edição do campo recém-criado (o card foca o editor inline
                    // assim que nascer).
                    const newFieldId = response?.fieldsId?.[0];
                    if (newFieldId) this._textEditing.requestEdit(newFieldId);
                },
                error: error => {
                    console.error(error);
                    this.editService.loading.set(false);
                },
            });

        // Chamamos o método para ocultar o menu de opções depois de solicitar o criação do item
        this.menuOptionsService.hideMenuOptions();
    }

    /**
     * Adiciona uma CAIXA DE DESTAQUE (CALLOUT). Nasce azul e vazia; o foco vai
     * para o título assim que o card aparecer.
     */
    public addCallout(): void {
        this.editService.loading.set(true);
        this._editItemsService
            .addCallout(
                this.menuOptionsService.aboveFieldId(),
                this.menuOptionsService.belowFieldId()
            )
            .pipe(finalize(() => this.editService.loading.set(false)))
            .subscribe({
                next: response => {
                    const newFieldId = response?.fieldsId?.[0];
                    if (newFieldId) this._focusWhenRendered(newFieldId, 'input.callout-title');
                },
                error: error => console.error(error),
            });

        this.menuOptionsService.hideMenuOptions();
    }

    /**
     * Adiciona uma GRADE DE INDICADORES (STATS) com três cards vazios; o foco vai
     * para o número do primeiro card assim que ele aparecer.
     */
    public addStats(): void {
        this.editService.loading.set(true);
        this._editItemsService
            .addStats(this.menuOptionsService.aboveFieldId(), this.menuOptionsService.belowFieldId())
            .pipe(finalize(() => this.editService.loading.set(false)))
            .subscribe({
                next: response => {
                    const newFieldId = response?.fieldsId?.[0];
                    if (newFieldId) this._focusWhenRendered(newFieldId, 'input.stat-value');
                },
                error: error => console.error(error),
            });

        this.menuOptionsService.hideMenuOptions();
    }

    /**
     * Adiciona uma TABELA (TABLE) com 2 colunas e 2 linhas vazias; o foco vai para o
     * nome da primeira coluna assim que o card aparecer.
     */
    public addTable(): void {
        this.editService.loading.set(true);
        this._editItemsService
            .addTable(this.menuOptionsService.aboveFieldId(), this.menuOptionsService.belowFieldId())
            .pipe(finalize(() => this.editService.loading.set(false)))
            .subscribe({
                next: response => {
                    const newFieldId = response?.fieldsId?.[0];
                    if (newFieldId) this._focusWhenRendered(newFieldId, 'th [data-cell="h-0"]');
                },
                error: error => console.error(error),
            });

        this.menuOptionsService.hideMenuOptions();
    }

    /**
     * Adiciona um SEMÁFORO (SEMAPHORE) com três itens vazios; o foco vai para o nome
     * do primeiro item assim que o card aparecer.
     */
    public addSemaphore(): void {
        this.editService.loading.set(true);
        this._editItemsService
            .addSemaphore(this.menuOptionsService.aboveFieldId(), this.menuOptionsService.belowFieldId())
            .pipe(finalize(() => this.editService.loading.set(false)))
            .subscribe({
                next: response => {
                    const newFieldId = response?.fieldsId?.[0];
                    if (newFieldId) this._focusWhenRendered(newFieldId, '[data-label="0"]');
                },
                error: error => console.error(error),
            });

        this.menuOptionsService.hideMenuOptions();
    }

    /**
     * Abre o assistente do GRÁFICO (CHART): diferente dos outros campos, o gráfico só é
     * criado no fim, depois de escolher o tipo e preencher os dados. Ele entra onde o menu
     * foi aberto.
     */
    public addChart(): void {
        if (this.editDataService.documentIsBlocked()) return;
        this._chartWizard.openCreate(this.menuOptionsService.aboveFieldId(), this.menuOptionsService.belowFieldId());
        this.menuOptionsService.hideMenuOptions();
    }

    /**
     * O card novo entra na lista pelo eco do servidor, um instante depois da
     * resposta: espera ele existir no DOM (poucas tentativas) e foca o título.
     */
    private _focusWhenRendered(fieldId: string, selector: string, attempt = 0): void {
        const input = document.querySelector<HTMLElement>(`[data-field-id="${fieldId}"] ${selector}`);

        if (input) {
            input.focus();
            return;
        }

        if (attempt >= 10) return;
        setTimeout(() => this._focusWhenRendered(fieldId, selector, attempt + 1), 150);
    }

    /**
     * Adiciona uma imagem
     */
    public addImages(): void {
        this.editService.loading.set(true);

        this._editItemsService
            .addImages(
                this.menuOptionsService.aboveFieldId(),
                this.menuOptionsService.belowFieldId()
            )
            .pipe(
                finalize(() => {
                    this.editService.loading.set(false);
                })
            )
            .subscribe({
                next: () => this.editService.loading.set(false),
                error: error => {
                    console.error(error);
                    this.editService.loading.set(false);
                },
            });

        // Chamamos o método para ocultar o menu de opções depois de solicitar o criação do item
        this.menuOptionsService.hideMenuOptions();
    }

    /**
     * Adiciona um vídeo
     */
    public addVideo(): void {
        this.editService.loading.set(true);

        this._editItemsService
            .addVideo(
                this.menuOptionsService.aboveFieldId(),
                this.menuOptionsService.belowFieldId()
            )
            .pipe(
                finalize(() => {
                    this.editService.loading.set(false);
                })
            )
            .subscribe({
                next: () => {
                    // Loading será desativado no finalize
                },
                error: error => {
                    console.error(error);
                    // Loading será desativado no finalize
                },
            });

        // Chamamos o método para ocultar o menu de opções depois de solicitar o criação do item
        this.menuOptionsService.hideMenuOptions();
    }

    public showFavoriteFields(): void {
        this.openFavoriteFields.emit();
        this.menuOptionsService.hideMenuOptions();
    }
}
